/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListGrantsRequest,
  ListGrantsSecurity,
} from "../models/operations/listgrants.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildGrantsQuery,
  GrantsQueryData,
  prefetchGrants,
  queryKeyGrants,
} from "./grants.core.js";
export {
  buildGrantsQuery,
  type GrantsQueryData,
  prefetchGrants,
  queryKeyGrants,
};

export type GrantsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listGrants access
 *
 * @remarks
 * List the current user's effective grants, including inherited role grants.
 */
export function useGrants(
  request?: ListGrantsRequest | undefined,
  security?: ListGrantsSecurity | undefined,
  options?: QueryHookOptions<GrantsQueryData, GrantsQueryError>,
): UseQueryResult<GrantsQueryData, GrantsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildGrantsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listGrants access
 *
 * @remarks
 * List the current user's effective grants, including inherited role grants.
 */
export function useGrantsSuspense(
  request?: ListGrantsRequest | undefined,
  security?: ListGrantsSecurity | undefined,
  options?: SuspenseQueryHookOptions<GrantsQueryData, GrantsQueryError>,
): UseSuspenseQueryResult<GrantsQueryData, GrantsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildGrantsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setGrantsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: GrantsQueryData,
): GrantsQueryData | undefined {
  const key = queryKeyGrants(...queryKeyBase);

  return client.setQueryData<GrantsQueryData>(key, data);
}

export function invalidateGrants(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listGrants", ...queryKeyBase],
  });
}

export function invalidateAllGrants(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listGrants"],
  });
}
